/**
 * How wide the choices grid's columns are, and the clipping the first one needs.
 *
 * Choice names are sentences ("Black Wrinkle Lift Latch with Snap Hooks"), and a
 * 220px column cut every one of them mid-word with no mark at all: glide-data-grid
 * 5.3 draws cells on a canvas and simply clips, there is not one ellipsis in the
 * whole published package. So the column is wide enough for the names merchants
 * actually write, and whatever still overflows is cut HERE, with the mark, and
 * handed to the hover tooltip whole.
 */
export const VALUE_COLUMN_WIDTHS = {
  name: 320,
  skuFragment: 120,
  // Wide enough for the heading itself: "Price change (USD)" was clipped at 130.
  priceDelta: 155,
  tierDelta: 120,
  imageUrl: 180,
};

/** glide draws cell text inside this much padding on each side (styles.ts:209). */
const CELL_HORIZONTAL_PADDING = 8;

export const columnTextWidth = (columnWidth: number) => columnWidth - CELL_HORIZONTAL_PADDING * 2;

export type MeasureText = (text: string) => number;

/**
 * The longest prefix that still fits once the mark itself is counted in. Binary
 * search rather than a character budget, because the grid's font is proportional
 * and a budget cuts "Illinois" and "WWWWWWWW" at the same place.
 */
export const ellipsize = (text: string, maxWidth: number, measure: MeasureText): string => {
  if (!text || maxWidth <= 0 || measure(text) <= maxWidth) {
    return text;
  }

  let fits = 0;
  let over = text.length;

  while (fits < over) {
    const mid = Math.ceil((fits + over) / 2);

    if (measure(`${text.slice(0, mid).trimEnd()}…`) <= maxWidth) {
      fits = mid;
    } else {
      over = mid - 1;
    }
  }

  return `${text.slice(0, fits).trimEnd()}…`;
};

let scratch: CanvasRenderingContext2D | null | undefined;

/**
 * Measures against the very font the grid draws with, which is the only honest
 * way to know what fits on a canvas. Null where there is no canvas at all (jsdom,
 * a server render), and the caller then shows the name WHOLE rather than cutting
 * it on a guess.
 */
export const gridTextMeasurer = (font: string): MeasureText | null => {
  if (scratch === undefined) {
    scratch = document.createElement("canvas").getContext("2d");
  }

  if (!scratch) {
    return null;
  }

  const context = scratch;

  return text => {
    context.font = font;

    return context.measureText(text).width;
  };
};

export interface WsmTierGroup {
  id: string;
  code: string;
  name: string;
}

/** A dealer column is named by the group ROW, which is what a delta points at. */
export const TIER_COLUMN_PREFIX = "tier:";

export const tierColumnId = (group: WsmTierGroup) => `${TIER_COLUMN_PREFIX}${group.id}`;

/** The group a dealer column belongs to, back out of the column id. */
export const tierColumnGroupId = (columnId: string) => columnId.slice(TIER_COLUMN_PREFIX.length);

/**
 * What a dealer column is headed with. The API hands the UI both a code and a
 * name; the NAME is the only one of the two a merchant ever wrote, so it heads
 * the column. The code stays as the secondary label, because prices and the
 * storefront key on it, and it is dropped when it IS the heading rather than
 * printed twice.
 */
export const tierColumnLabel = (group: WsmTierGroup) => {
  const name = group.name.trim();

  return {
    title: name || group.code,
    code: name && name !== group.code ? group.code : "",
  };
};
